// What the two workspaces of the maker share (src/ui/make.ts, src/ui/tab5.ts):
// the icons, the maker's face, the field a message is typed in, and what the
// chat says when the credits are too few.

import { CREDITS, MESSAGE_MAX, type CreditsInfo } from "../core/make.ts";
import { MEMBERSHIP_SIGNUPS_OPEN } from "../core/membership.ts";
import { h, svg } from "./dom.ts";
import { lang, t } from "./lang.ts";

export const ICON = {
  send: `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 5.500 15.500 10 11 14.500" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  restart: `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 10a5.500 5.500 0 1 0 1.800-4.100M4.500 3.500v3.300h3.300" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  full: `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.500 7.500v-4h4M16.500 7.500v-4h-4M3.500 12.500v4h4M16.500 12.500v4h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  tab5: `<svg viewBox="0 0 20 20" aria-hidden="true"><rect x="5" y="1.800" width="10" height="16.400" rx="2.400" fill="none" stroke="currentColor" stroke-width="1.900"/><rect x="7.200" y="4.200" width="5.600" height="9.600" rx=".8" fill="currentColor" opacity=".35"/><circle cx="10" cy="15.800" r=".9" fill="currentColor"/></svg>`,
  close: `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
};

/** The maker's little face: the chat's speaker, drawn in the room's colours. */
export const FACE = `<svg viewBox="0 0 40 40" aria-hidden="true"><rect x="5" y="9" width="30" height="24" rx="9" fill="#ffb45c" stroke="#221338" stroke-width="2.600"/><rect x="18.700" y="3" width="2.600" height="7" rx="1.300" fill="#221338"/><circle cx="20" cy="3.600" r="2.600" fill="#ff5f9e" stroke="#221338" stroke-width="1.600"/><circle cx="14.500" cy="20" r="2.700" fill="#221338"/><circle cx="25.500" cy="20" r="2.700" fill="#221338"/><path d="M15.500 26.200q4.500 3 9 0" fill="none" stroke="#221338" stroke-width="2.200" stroke-linecap="round"/></svg>`;

/** Keys typed into a field are the field's: the room's own keys must not hear them. */
export function keepKeys(field: HTMLElement): void {
  for (const type of ["keydown", "keyup"] as const) field.addEventListener(type, (event) => event.stopPropagation());
}

/** A field that grows with what is typed, up to `max` lines; Enter sends and Shift+Enter breaks the line. */
export function prompter(placeholder: string, onSend: () => void, max = 6): HTMLTextAreaElement {
  const field = h("textarea", { class: "make-field", rows: "1", placeholder, maxlength: String(MESSAGE_MAX), "aria-label": placeholder });
  keepKeys(field);
  const fit = () => {
    field.style.height = "auto";
    const line = parseFloat(getComputedStyle(field).lineHeight) || 22;
    // An empty field is as tall as its placeholder, which may wrap on a narrow screen or in a longer language.
    let height = field.scrollHeight;
    if (!field.value && field.placeholder) {
      field.value = field.placeholder;
      height = field.scrollHeight;
      field.value = "";
    }
    field.style.height = `${Math.min(height, line * max + 20)}px`;
  };
  field.addEventListener("input", fit);
  addEventListener("resize", fit);
  field.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      onSend();
    }
  });
  queueMicrotask(fit);
  return field;
}

/** The chat's answer when the visitor has too few credits for what they asked: what it costs, what they have, where more come from. */
export function outOfCreditsMessage(price: number, credits: CreditsInfo): HTMLElement {
  const more = credits.member && credits.renews
    ? t("make.out.renews", { date: new Date(credits.renews).toLocaleDateString(lang(), { month: "long", day: "numeric" }) })
    : MEMBERSHIP_SIGNUPS_OPEN ? t("make.out.member", { n: CREDITS.monthly }) : t("make.out.paused");
  return h("li", { class: "make-msg maker failed" }, h("span", { class: "make-face" }, svg(FACE)),
    h("div", {}, h("p", { text: t("make.out", { price, balance: credits.balance }) }), h("p", { class: "note", text: more })));
}
